import type { BoardData, CollectionName } from '../model/schema.js';
import { COLLECTIONS } from '../model/schema.js';
import { deepEqual } from './deep-equal.js';

export interface EntityChange {
  id: string;
  /** Top-level fields that differ (added, removed or changed), sorted. */
  fields: string[];
}

export interface CollectionDiff {
  added: string[];
  removed: string[];
  changed: EntityChange[];
}

export interface BoardDiff {
  /** Changed `meta` fields. */
  meta: string[];
  collections: Record<CollectionName, CollectionDiff>;
  /** True when nothing differs. */
  empty: boolean;
}

/** Top-level keys whose values differ between two plain objects, sorted. */
export function changedFields(a: object, b: object): string[] {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys]
    .filter((k) => !deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
    .sort();
}

function diffCollection(a: Record<string, object>, b: Record<string, object>): CollectionDiff {
  const added = Object.keys(b)
    .filter((id) => !Object.hasOwn(a, id))
    .sort();
  const removed = Object.keys(a)
    .filter((id) => !Object.hasOwn(b, id))
    .sort();
  const changed = Object.keys(a)
    .filter((id) => Object.hasOwn(b, id))
    .sort()
    .map((id) => ({ id, fields: changedFields(a[id]!, b[id]!) }))
    .filter((c) => c.fields.length > 0);
  return { added, removed, changed };
}

/**
 * Compares two boards (e.g. a snapshot with HEAD): per collection the added, removed and changed
 * entity ids with the names of changed fields; ids are sorted.
 */
export function diffBoards(a: BoardData, b: BoardData): BoardDiff {
  const collections = Object.fromEntries(
    COLLECTIONS.map((name) => [name, diffCollection(a[name], b[name])]),
  ) as Record<CollectionName, CollectionDiff>;
  const meta = changedFields(a.meta, b.meta);
  const empty =
    meta.length === 0 &&
    Object.values(collections).every(
      (c) => c.added.length + c.removed.length + c.changed.length === 0,
    );
  return { meta, collections, empty };
}
